"use client";

import clsx from "clsx";
import {usePathname, useRouter, useSearchParams} from "next/navigation";
import {combinedApprovalStatus, resolveToolApprovalStatus} from "@repo/ui/gailgpt/tool-approval-status";
import {useMutationProposalOutcomes} from "@/contexts/chat/MutationProposalOutcomesContext";
import {ReviewConversationContext, useMutationProposalReview, type MutationProposalReview} from "@/contexts/chat/MutationProposalReviewContext";
import {editsProposalTarget, mutationProposalHref, mutationProposalReviewParams, reviewEditorPage, type MutationProposal} from "@/services/wingman/mutation-proposal";
import {UnsavedDataContext} from "@/hooks/useHasUnsavedData";
import {useContext, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import Icon from "@/components/library/Icon";
import type {AssistantContentPart} from "@/types/chatMessages";
import {useArtifactPanel} from "@/contexts/chat/ArtifactPanelContext";
import type {ToolApprovalResponse} from "@/services/wingman";
import MutationProposalReviewPanel from "./MutationProposalReviewPanel";
import ToolApprovalCard from "./ToolApprovalCard";

export type ApprovalPart = Extract<AssistantContentPart, {type: "functionCall"}>;

/**
 * The editor already showing the target hosts its review in place, including a script revision.
 * Keeping the current path and query, whatever its ID's case, keeps the page and its draft mounted.
 */
function inPlaceReviewHref(pathname: string | null, search: string | undefined, proposal: MutationProposal, conversationId: string): string | undefined {
   const page = reviewEditorPage(pathname);
   if (!pathname || !page || !editsProposalTarget(page, proposal)) return;
   const query = new URLSearchParams(search);
   mutationProposalReviewParams(proposal, conversationId).forEach((value, key) => query.set(key, value));
   return `${pathname}?${query}`;
}

/** This proposal is the one the current page is reviewing. */
function proposalMatchesPageReview(proposal: MutationProposal, pageReview: MutationProposalReview, conversationId?: string): boolean {
   return proposal.proposalId === pageReview.proposalId && conversationId === pageReview.conversationId && editsProposalTarget(pageReview, proposal);
}

/** The chat owns live approval state; the shared panel only hosts its review UI. */
export default function ApprovalReview({parts, conversationId, messageId, approvalMessageIds, activeMessageId, disabled, onRespond, onRequestFresh}: {
   parts: ApprovalPart[];
   conversationId?: string;
   messageId?: string;
   approvalMessageIds?: Record<string, string>;
   activeMessageId?: string;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<void>;
   onRequestFresh?: (proposal: MutationProposal) => Promise<void>;
}) {
   const {t} = useTranslation("ask");
   const router = useRouter();
   const pathname = usePathname();
   const search = useSearchParams();
   const pageReview = useMutationProposalReview();
   const reportOutcome = useMutationProposalOutcomes()?.report;
   const reviewConversation = useContext(ReviewConversationContext);
   const shouldFinishReview = useRef(false);
   const {checkBeforeAction} = useContext(UnsavedDataContext);
   const {activeArtifact, openArtifact, closeArtifact, approvalPanelHost} = useArtifactPanel();
   const busy = useRef(false);
   const [pendingApprovalId, setPendingApprovalId] = useState<string | null>(null);
   const pending = pendingApprovalId !== null;
   const id = `approval:${parts[0].data.approval!.id}`;
   const isOpen = activeArtifact?.kind === "approval" && activeArtifact.id === id;
   const title = parts.length === 1
      ? parts[0].data.approval!.proposal?.summary ?? parts[0].data.functionName
      : t("toolApproval.proposals", {count: parts.length});
   const isActive = (part: ApprovalPart) => !!messageId &&
      (approvalMessageIds?.[part.data.approval!.id] ?? messageId) === activeMessageId;
   // A decision in flight is reported as submitting, so the page holds saves before the server answers.
   const statuses = parts.map(part => resolveToolApprovalStatus({approval: part.data.approval,
      completed: part.isCompleted, active: isActive(part), result: part.result, pending: part.data.approval!.id === pendingApprovalId}));
   const status = combinedApprovalStatus(statuses, pending);

   const editorPart = parts.find(part => {
      const proposal = part.data.approval?.proposal;
      return !!proposal && !!mutationProposalHref(proposal, conversationId);
   });
   const preview = editorPart?.data.approval?.proposal;
   const canonicalHref = preview ? mutationProposalHref(preview, conversationId) : undefined;
   const inPlaceHref = preview && canonicalHref ? inPlaceReviewHref(pathname, search?.toString(), preview, conversationId!) : undefined;
   const matchingPart = parts.find(part => {
      const proposal = part.data.approval?.proposal;
      return pageReview != null && proposal != null && proposalMatchesPageReview(proposal, pageReview, conversationId);
   });
   const matchingStatus = matchingPart ? statuses[parts.indexOf(matchingPart)] : undefined;
   const matchingCapability = matchingPart?.data.approval?.proposal?.capability;
   const setHasReview = pageReview?.setHasReview;
   useEffect(() => {
      if (!matchingPart || !setHasReview) return;
      setHasReview(true);
      return () => setHasReview(false);
   }, [matchingPart, setHasReview]);
   // Approved is still awaiting execution, so only a settled outcome releases the editor.
   const decisionPending = matchingStatus === "required" || matchingStatus === "submitting" || matchingStatus === "approved";
   const setDecisionPending = pageReview?.setDecisionPending;
   useEffect(() => {
      if (!matchingPart || !setDecisionPending) return;
      setDecisionPending(decisionPending);
      return () => setDecisionPending(true);
   }, [matchingPart, setDecisionPending, decisionPending]);
   // Editors stay mounted through a decision and may outlive this conversation, so the page
   // tracks every outcome: it holds their saves during execution and refreshes them after.
   useEffect(() => {
      if (!reportOutcome) return;
      parts.forEach((part, index) => {
         const {id: approvalId, proposal} = part.data.approval!;
         if (proposal && mutationProposalHref(proposal, conversationId)) reportOutcome(approvalId, proposal, statuses[index]);
      });
   });
   const finish = pageReview?.finish;
   useEffect(() => {
      // An Action review stays open after a decision so its saved outcome can be inspected.
      if (matchingCapability !== "use_cases_update" && finish && shouldFinishReview.current && !disabled && (matchingStatus === "completed" || matchingStatus === "denied")) {
         // Consume this decision's automatic close so manually reopening stays open.
         shouldFinishReview.current = false;
         finish();
      }
   }, [matchingCapability, finish, disabled, matchingStatus]);

   // A route change must not leave an orphaned proposal in the shared panel.
   useEffect(() => () => closeArtifact(id), [closeArtifact, id]);

   const respond = async (decision: ToolApprovalResponse) => {
      if (busy.current || disabled || !onRespond) return;
      shouldFinishReview.current = true;
      busy.current = true;
      setPendingApprovalId(decision.id);
      // The deciding surface reports a failed submission; the proposal stays open to retry.
      try {
         await onRespond(decision);
      } finally {
         busy.current = false;
         setPendingApprovalId(null);
      }
   };

   return <>
      <section aria-label={t("toolApproval.title")} className={clsx("my-3 overflow-clip rounded-xl border", matchingPart ? "border-primary bg-blue-50 dark:bg-blue-950/30" : "border-300 bg-panel")}>
         <button type="button" aria-expanded={!!matchingPart || isOpen}
            aria-current={matchingPart ? "true" : undefined}
            onClick={async () => {
               // On a phone this conversation covers the editor it is about to review in.
               if (inPlaceHref) { reviewConversation?.showPage(); router.push(inPlaceHref); }
               else if (canonicalHref) { if (await checkBeforeAction()) router.push(canonicalHref); }
               else openArtifact({kind: "approval", id, title});
            }}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-300 bg-100 text-600">
               <Icon icon={status === "completed" ? "check" : "file-lines"} className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
               <span className="block break-words text-sm font-semibold text-800">{title}</span>
               <output className="mt-1 block text-xs text-500">{t(`toolApproval.${status}`)}</output>
            </span>
            <span className="shrink-0 text-xs font-medium text-700">{t("toolApproval.review")}</span>
            <Icon icon="chevron-right" className="h-3 w-3 shrink-0 text-400" />
         </button>
         {!matchingPart && !isOpen && parts.filter((part, index) => !!part.data.approval?.proposal && statuses[index] !== "completed" && statuses[index] !== "denied").map(part => <ToolApprovalCard embedded key={part.data.approval!.id}
            call={part.data} completed={part.isCompleted} result={part.result} active={isActive(part)}
            disabled={disabled || pending} onRespond={onRespond ? respond : undefined} onRequestFresh={onRequestFresh} />)}
      </section>
      {matchingPart && pageReview?.host && createPortal(
         <MutationProposalReviewPanel call={matchingPart.data} completed={matchingPart.isCompleted} result={matchingPart.result}
            active={isActive(matchingPart)} disabled={disabled || pending} onRespond={onRespond ? respond : undefined} onRequestFresh={onRequestFresh}
            onClose={pageReview.finish} />,
         pageReview.host)}
      {isOpen && approvalPanelHost && createPortal(
         <div className="h-full min-h-0 overflow-y-auto overscroll-contain px-4 py-1">
            {parts.map(part => <ToolApprovalCard key={part.data.approval!.id}
               call={part.data} completed={part.isCompleted} result={part.result} active={isActive(part)}
               disabled={disabled || pending} onRespond={onRespond ? respond : undefined} onRequestFresh={onRequestFresh} />)}
         </div>, approvalPanelHost)}
   </>;
}
